import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const PHASES = [
  {
    id: 0,
    headline: "From the moment I saw you…",
    body: "Something shifted. Like the world exhaled and everything suddenly made a little more sense. You walked in and I forgot how to be ordinary.",
    note: "you changed everything",
    accent: '#F2A7A7',
    ariImg: '/ari1.jpg',
    zainabImg: '/zainab1.jpg',
  },
  {
    id: 1,
    headline: "You are the kind of rare…",
    body: "That poets spend lifetimes searching for. Warm. Brilliant. Effortlessly yourself. And somehow, impossibly — here, in front of me.",
    note: "one of one",
    accent: '#D4526E',
    ariImg: '/ari2.jpg',
    zainabImg: '/zainab2.jpg',
  },
  {
    id: 2,
    headline: "I think about you in quiet moments…",
    body: "When the world slows down, when a song comes on, when something beautiful happens — you're the first person I want to tell.",
    note: "always you",
    accent: '#C9A96E',
    ariImg: '/ari3.jpg',
    zainabImg: '/zainab3.jpg',
  },
  {
    id: 3,
    headline: "And so I had to say it…",
    body: "Not in a whisper, not by accident — but like this. Intentionally. Boldly. Because you deserve to know exactly how someone feels about you.",
    note: "no more hiding it",
    accent: '#F2A7A7',
    ariImg: '/ari4.jpg',
    zainabImg: '/zainab4.jpg',
  },
];

function PhotoFrame({ src, label, side, accent }) {
  const tilt = side === 'left' ? -4 : 4;
  return (
    <div className="flex flex-col items-center gap-3">
      <motion.div
        initial={{ rotate: side === 'left' ? -18 : 18, scale: 0.6, opacity: 0, x: side === 'left' ? -60 : 60 }}
        animate={{ rotate: tilt, scale: 1, opacity: 1, x: 0 }}
        transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1], delay: side === 'left' ? 0.1 : 0.22 }}
        style={{
          position: 'relative',
          width: 'clamp(110px, 20vw, 175px)',
          aspectRatio: '3/4',
          borderRadius: 16,
          overflow: 'hidden',
          boxShadow: `0 24px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(201,169,110,0.22), 0 0 40px ${accent}28`,
          border: '1px solid rgba(201,169,110,0.2)',
        }}
      >
        <img
          src={src}
          alt={label}
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }}
        />
        {/* Overlay sheen */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, transparent 35%, rgba(0,0,0,0.3) 100%)'
        }} />
      </motion.div>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.45 }}
        transition={{ delay: 0.5 }}
        style={{ fontFamily: "'Lato', sans-serif", fontSize: '0.65rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: '#FDF6F0' }}
      >
        {label}
      </motion.p>
    </div>
  );
}

function HeartDivider({ color }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '0 10px' }}>
      <div style={{ width: 1, height: 36, background: `linear-gradient(to bottom, transparent, ${color}70)` }} />
      <motion.span
        animate={{ scale: [1, 1.25, 1], opacity: [0.7, 1, 0.7] }}
        transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
        style={{ fontSize: 22, lineHeight: 1 }}
      >♥</motion.span>
      <div style={{ width: 1, height: 36, background: `linear-gradient(to top, transparent, ${color}70)` }} />
    </div>
  );
}

export default function CollagePage({ onFinished }) {
  const [currentPhase, setCurrentPhase] = useState(0);
  const [direction, setDirection] = useState(1);
  const [auto, setAuto] = useState(true);

  useEffect(() => {
    if (!auto) return;
    const t = setInterval(() => {
      setCurrentPhase(prev => {
        if (prev >= PHASES.length - 1) { clearInterval(t); return prev; }
        setDirection(1);
        return prev + 1;
      });
    }, 5500);
    return () => clearInterval(t);
  }, [auto]);

  const goTo = (idx) => { setAuto(false); setDirection(idx > currentPhase ? 1 : -1); setCurrentPhase(idx); };
  const goNext = () => {
    if (currentPhase < PHASES.length - 1) { setDirection(1); setCurrentPhase(p => p + 1); setAuto(false); }
    else onFinished();
  };
  const goPrev = () => {
    if (currentPhase > 0) { setDirection(-1); setCurrentPhase(p => p - 1); setAuto(false); }
  };

  const phase = PHASES[currentPhase];

  const slideVariants = {
    enter: d => ({ x: d > 0 ? '40%' : '-40%', opacity: 0, scale: 0.93 }),
    center: { x: 0, opacity: 1, scale: 1, transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] } },
    exit: d => ({ x: d > 0 ? '-40%' : '40%', opacity: 0, scale: 0.93, transition: { duration: 0.45 } }),
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      {/* Phase glow BG */}
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        background: `radial-gradient(ellipse 80% 70% at 50% 50%, ${phase.accent}18 0%, transparent 70%)`,
        transition: 'background 1s ease',
      }} />

      {/* Phase dots top */}
      <div style={{ position: 'fixed', top: 32, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 8, zIndex: 20 }}>
        {PHASES.map((_, i) => (
          <button key={i} onClick={() => goTo(i)}
            style={{
              width: i === currentPhase ? 24 : 7, height: 7, borderRadius: 99,
              background: i === currentPhase ? phase.accent : 'rgba(253,246,240,0.2)',
              border: 'none', cursor: 'pointer', transition: 'all 0.4s ease',
              padding: 0,
            }}
          />
        ))}
      </div>

      {/* Main animated card */}
      <div style={{ position: 'relative', width: '100%', maxWidth: 600, minHeight: 520, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
        <AnimatePresence custom={direction} mode="wait">
          <motion.div
            key={currentPhase}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            style={{ position: 'absolute', width: '100%', padding: '0 24px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
          >
            {/* Photos */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 36 }}>
              <PhotoFrame src={phase.ariImg} label="ari" side="left" accent={phase.accent} />
              <HeartDivider color={phase.accent} />
              <PhotoFrame src={phase.zainabImg} label="zainab" side="right" accent={phase.accent} />
            </div>

            {/* Text */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.38, duration: 0.65 }}
              style={{ textAlign: 'center', maxWidth: 420 }}
            >
              <p style={{ fontFamily: "'Lato', sans-serif", fontSize: '0.65rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: phase.accent, marginBottom: 10, opacity: 0.85 }}>
                {phase.note}
              </p>
              <h2 style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 'clamp(1.55rem, 4vw, 2.3rem)',
                fontWeight: 500, fontStyle: 'italic',
                color: '#FDF6F0', lineHeight: 1.2, marginBottom: 14
              }}>
                {phase.headline}
              </h2>
              <p style={{
                fontFamily: "'Lato', sans-serif", fontWeight: 300,
                fontSize: 'clamp(0.82rem, 2vw, 0.95rem)',
                color: 'rgba(253,246,240,0.6)', lineHeight: 1.85
              }}>
                {phase.body}
              </p>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation bar */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 30,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 32px 32px',
        background: 'linear-gradient(to top, rgba(26,10,16,0.97) 60%, transparent)',
      }}>
        <button onClick={goPrev} disabled={currentPhase === 0}
          style={{ fontFamily: "'Lato'", fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#C9A96E', background: 'none', border: 'none', cursor: 'pointer', opacity: currentPhase === 0 ? 0.2 : 1 }}>
          ← prev
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          {PHASES.map((_, i) => (
            <button key={i} onClick={() => goTo(i)}
              style={{ width: i === currentPhase ? 20 : 7, height: 7, borderRadius: 99, background: i === currentPhase ? phase.accent : 'rgba(253,246,240,0.25)', border: 'none', cursor: 'pointer', transition: 'all 0.3s', padding: 0 }} />
          ))}
        </div>
        <button onClick={goNext}
          style={{ fontFamily: "'Lato'", fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#C9A96E', background: 'none', border: 'none', cursor: 'pointer' }}>
          {currentPhase === PHASES.length - 1 ? 'the question →' : 'next →'}
        </button>
      </div>
    </div>
  );
}
